import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "@/context";
import { useLanguageContext } from "@/context/LanguageContext";
import { useFileValidator } from "@/hooks/useValidateFile";
import { Doc, QuestionElement } from "@/components/FoodSafety/components/FoodSafetyQuestionnaire/types";

// HOOK 1: useFoodSafetyFormState
export const useFoodSafetyFormState = () => {
  const { darkMode } = useTheme();
  const { languageText } = useLanguageContext();
  const [requiredDocs, setRequiredDocs] = useState<Doc[]>([]);
  const [resetTrigger, setResetTrigger] = useState<number>(0);
  // Track docs by component key to handle cleanup when selections change
  const docsByComponentKey = useRef<Map<string, Doc[]>>(new Map());

  const handleRequiredDocs = useCallback((newDocs: Doc[], componentKey?: string) => {
    setRequiredDocs(prevDocs => {
      // Use componentKey to track and replace docs from this component
      if (componentKey !== undefined) {
        const key = componentKey || '__root__';

        if (newDocs.length > 0) {
          // Replace this level: remove prior subtree under this key, then set new docs
          const keysToDelete: string[] = [key];
          docsByComponentKey.current.forEach((_docs, existingKey) => {
            if (existingKey !== key && existingKey.startsWith(key + '-')) {
              keysToDelete.push(existingKey);
            }
          });
          keysToDelete.forEach(k => docsByComponentKey.current.delete(k));
          docsByComponentKey.current.set(key, newDocs);
        } else {
          docsByComponentKey.current.delete(key);
        }
        
        // Rebuild the complete list from all active components
        const allDocs: Doc[] = [];
        docsByComponentKey.current.forEach((docs) => {
          allDocs.push(...docs);
        });
        
        // Deduplicate by id
        const uniqueDocs = new Map(allDocs.map(doc => [doc.id, doc]));
        return Array.from(uniqueDocs.values());
      } else {
        // Legacy behavior: merge without tracking (for backward compatibility)
        const allDocs = [...prevDocs, ...newDocs];
        const uniqueDocs = new Map(allDocs.map(doc => [doc.id, doc]));
        return Array.from(uniqueDocs.values());
      }
    });
  }, []);

  const resetForm = () => {
    setRequiredDocs([]);
    docsByComponentKey.current.clear();
    setResetTrigger(prev => prev + 1);
  };

  return {
    darkModeClass: darkMode ? "dark" : "light",
    formHeaders: languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.FOOD_SAFETY_HEADERS,
    requiredDocs,
    handleRequiredDocs,
    resetTrigger,
    resetForm
  };
};

/** First selected answer that drives a nested QUESTION branch (matches validation path logic). */
export const getPrimaryBranchingAnswer = (
  element: QuestionElement,
  selectedAnswer: string | string[],
): string | null => {
  if (!element?.ANSWER) return null;

  if (Array.isArray(selectedAnswer)) {
    for (const answer of selectedAnswer) {
      if (element.ANSWER[answer]?.QUESTION) {
        return answer;
      }
    }
    return null;
  }

  if (selectedAnswer && element.ANSWER[selectedAnswer]?.QUESTION) {
    return selectedAnswer;
  }
  return null;
};

// HOOK 2: useQuestionnaire
export const useQuestionnaire = ({
  element,
  onRequiredDocs,
  questionNumber = 1,
  componentKey = "",
}: {
  element: QuestionElement;
  onRequiredDocs?: (docs: Doc[], componentKey?: string) => void;
  questionNumber?: number;
  componentKey?: string;
}, initialAnswersByTitle?: Record<string, string>, initialPromptByTitle?: Record<string, string>) => {
  const { darkMode } = useTheme();
  const darkModeClass = darkMode ? "dark" : "light";

  const [selectedAnswer, setSelectedAnswer] = useState<string | string[]>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [currentDocs, setCurrentDocs] = useState<Doc[]>([]);

  const { ANSWER } = element || {};
  const answerEntries = Object.entries(ANSWER || {}).filter(([key]) => key !== "promptField");
  const promptField = element?.promptField || ANSWER?.promptField;
  const isMultichoice = element.multiSelect;

  // Check if we should show promptField based on selected answer's nested question
  const shouldShowPromptField = () => {
    // First check if current element has promptField
    if (promptField) return promptField;
    
    if (isMultichoice) {
      if (Array.isArray(selectedAnswer) && selectedAnswer.length > 0) {
        // For multichoice, check if any selected answer has a promptField
        for (const answer of selectedAnswer) {
          const answerElement = ANSWER?.[answer];
          if (answerElement?.promptField) {
            return answerElement.promptField;
          }
          // Also check nested questions
          if (answerElement?.QUESTION?.promptField) {
            return answerElement.QUESTION.promptField;
          }
        }
      }
    } else if (selectedAnswer) {
      // For single choice, check if selected answer has a promptField
      const answerElement = ANSWER?.[selectedAnswer as string];
      if (answerElement?.promptField) {
        return answerElement.promptField;
      }
      // Also check nested questions
      if (answerElement?.QUESTION?.promptField) {
        return answerElement.QUESTION.promptField;
      }
    }
    
    return null;
  };

  const currentPromptField = shouldShowPromptField();

  const hasPrefilledRef = useRef<boolean>(false);
  const hasPrefilledPromptRef = useRef<boolean>(false);

  // Track previous prefill value to detect when it changes
  const previousPrefillRef = useRef<string | undefined>(undefined);
  
  // Reset prefill refs when initialAnswersByTitle changes to allow re-prefilling
  // This enables restoring original answers when user reverts changes
  useEffect(() => {
    const titleKey = element?.title || "";
    const currentPrefill = initialAnswersByTitle && titleKey ? initialAnswersByTitle[titleKey] : undefined;
    const prefillStr = currentPrefill !== undefined && currentPrefill !== null ? String(currentPrefill) : undefined;
    
    // If prefill value changed (from undefined to a value, or vice versa, or different value)
    // and we have a prefill now, reset the ref to allow re-prefilling
    if (prefillStr !== previousPrefillRef.current && prefillStr !== undefined && prefillStr !== "") {
      hasPrefilledRef.current = false;
    }
    
    previousPrefillRef.current = prefillStr;
  }, [initialAnswersByTitle, element?.title]);

  useEffect(() => {
    if (hasPrefilledRef.current) return;
    const titleKey = element?.title || "";
    const prefill = initialAnswersByTitle && titleKey ? initialAnswersByTitle[titleKey] : undefined;
    if (prefill !== undefined && prefill !== null && prefill !== "") {
      if (isMultichoice) {
        // Parse multi-select answers - handle nested array strings
        let parsedAnswers: string[] = [];
        try {
          // Try to parse as JSON first (for nested arrays)
          const parsed = JSON.parse(prefill);
          if (Array.isArray(parsed)) {
            // Flatten nested arrays and extract unique values
            const flatten = (arr: any[]): string[] => {
              return arr.reduce((acc, val) => {
                if (Array.isArray(val)) {
                  acc.push(...flatten(val));
                } else if (typeof val === 'string' && val.trim()) {
                  acc.push(val.trim());
                }
                return acc;
              }, []);
            };
            parsedAnswers = Array.from(new Set(flatten(parsed))); // Remove duplicates
          } else if (typeof parsed === 'string') {
            parsedAnswers = [parsed];
          }
        } catch (parseError) {
          // If JSON parsing fails, try to handle mixed format like "[[Household], Low Acid Canned Food/Acidified Food]"

          
          // Check if it looks like a mixed array format
          if (prefill.startsWith('[') && prefill.endsWith(']')) {
            try {
              // Try to extract individual elements manually
              let content = prefill.slice(1, -1); // Remove outer brackets
              const elements: string[] = [];
              
              // Handle nested arrays and plain strings
              let depth = 0;
              let currentElement = '';
              let inQuotes = false;
              
              for (let i = 0; i < content.length; i++) {
                const char = content[i];
                
                if (char === '"' && (i === 0 || content[i-1] !== '\\')) {
                  inQuotes = !inQuotes;
                  currentElement += char;
                } else if (!inQuotes) {
                  if (char === '[') {
                    depth++;
                    currentElement += char;
                  } else if (char === ']') {
                    depth--;
                    currentElement += char;
                  } else if (char === ',' && depth === 0) {
                    // Found a top-level separator
                    if (currentElement.trim()) {
                      elements.push(currentElement.trim());
                    }
                    currentElement = '';
                  } else {
                    currentElement += char;
                  }
                } else {
                  currentElement += char;
                }
              }
              
              // Add the last element
              if (currentElement.trim()) {
                elements.push(currentElement.trim());
              }
              
              // Parse each element
              elements.forEach(element => {
                try {
                  // Try to parse as JSON (for nested arrays)
                  const parsed = JSON.parse(element);
                  if (Array.isArray(parsed)) {
                    parsedAnswers.push(...parsed.filter(item => typeof item === 'string' && item.trim()));
                  } else if (typeof parsed === 'string' && parsed.trim()) {
                    parsedAnswers.push(parsed.trim());
                  }
                } catch {
                  // If not JSON, treat as plain string
                  const cleaned = element.replace(/^["']|["']$/g, '').trim(); // Remove quotes
                  if (cleaned) {
                    parsedAnswers.push(cleaned);
                  }
                }
              });
              
              // Remove duplicates
              parsedAnswers = Array.from(new Set(parsedAnswers));
              
            } catch (mixedParseError) {
              // Final fallback: comma-separated or single value
              if (prefill.includes(',')) {
                parsedAnswers = prefill.split(',').map((s: string) => s.trim()).filter(Boolean);
              } else {
                parsedAnswers = [prefill];
              }
            }
          } else {
            // Standard fallback: comma-separated or single value
            if (prefill.includes(',')) {
              parsedAnswers = prefill.split(',').map((s: string) => s.trim()).filter(Boolean);
            } else {
              parsedAnswers = [prefill];
            }
          }
        }

        setSelectedAnswer(parsedAnswers);
      } else {
        setSelectedAnswer(prefill);
      }
      hasPrefilledRef.current = true;
    }
  }, [initialAnswersByTitle, element?.title, isMultichoice]);

  // Track previous prompt prefill value to detect when it changes
  const previousPromptPrefillRef = useRef<string | undefined>(undefined);
  
  // Reset prompt prefill ref when initialPromptByTitle changes to allow re-prefilling
  useEffect(() => {
    const titleKey = element?.title || "";
    const currentPromptPrefill = initialPromptByTitle && titleKey ? initialPromptByTitle[titleKey] : undefined;
    const promptPrefillStr = currentPromptPrefill !== undefined && currentPromptPrefill !== null ? String(currentPromptPrefill) : undefined;
    
    // If prompt prefill value changed (from undefined to a value, or vice versa, or different value)
    // and we have a prefill now, reset the ref to allow re-prefilling
    if (promptPrefillStr !== previousPromptPrefillRef.current && promptPrefillStr !== undefined && promptPrefillStr !== "") {
      hasPrefilledPromptRef.current = false;
    }
    
    previousPromptPrefillRef.current = promptPrefillStr;
  }, [initialPromptByTitle, element?.title]);

  // Prefill prompt field value (e.g., FEI number) if provided
  useEffect(() => {
    if (hasPrefilledPromptRef.current) return;
    const titleKey = element?.title || "";
    const prefillPrompt = initialPromptByTitle && titleKey ? initialPromptByTitle[titleKey] : undefined;
    if (prefillPrompt !== undefined && prefillPrompt !== null && prefillPrompt !== "") {
      setInputValue(String(prefillPrompt));
      hasPrefilledPromptRef.current = true;
    }
  }, [initialPromptByTitle, element?.title]);

  const onRequiredDocsRef = useRef(onRequiredDocs);
  onRequiredDocsRef.current = onRequiredDocs;

  useEffect(() => {
    const keyAtEffect = componentKey;
    return () => {
      onRequiredDocsRef.current?.([], keyAtEffect);
    };
  }, [componentKey]);

  // If parent path changes (componentKey updates) and there is no prefill for this question,
  // clear any previously selected answer so the user must choose anew.
  useEffect(() => {
    const titleKey = element?.title || "";
    const hasPrefill = !!(initialAnswersByTitle && titleKey && initialAnswersByTitle[titleKey]);
    if (!hasPrefill) {
      setSelectedAnswer(isMultichoice ? [] : "");
      setInputValue("");
      hasPrefilledRef.current = false;
      // Clear docs when componentKey changes and there's no prefill (selection was cleared)
      setCurrentDocs([]);
      onRequiredDocs?.([], componentKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentKey]);

  // Error validation removed - will be handled by new centralized system

  // Collect DOCS from all selected answers at the current level only
  // Nested components will collect their own DOCS and report via onRequiredDocs
  const collectDocs = useCallback((currentElement: QuestionElement | undefined, answersToUse?: string | string[]): Doc[] => {
    const docs: Doc[] = [];
    if (!currentElement || !currentElement.ANSWER) return docs;

    // Use provided answers or fall back to selectedAnswer
    const answers = answersToUse ?? selectedAnswer;

    if (isMultichoice) {
      if (Array.isArray(answers)) {
        answers.forEach(answer => {
          const answerElement = currentElement.ANSWER?.[answer];
          if (!answerElement) return;
          if (answerElement.disabled) return;

          // Collect DOCS from current answer (even if it has nested questions)
          // Nested components will collect their own DOCS independently
          if (answerElement.DOCS) {
            const docList = Array.isArray(answerElement.DOCS)
              ? answerElement.DOCS
              : [answerElement.DOCS];
            docs.push(...docList);
          }
          // Note: We do NOT recursively collect from nested questions here
          // because nested QuestionnaireComp components have their own state
          // and will call onRequiredDocs independently
        });
      }
    } else {
      if (!answers) return docs;

      const answerElement = currentElement.ANSWER[answers as string];
      if (!answerElement) return docs;
      if (answerElement.disabled) return docs;

      // Collect DOCS from current answer (even if it has nested questions)
      // Nested components will collect their own DOCS independently
      if (answerElement.DOCS) {
        const docList = Array.isArray(answerElement.DOCS)
          ? answerElement.DOCS
          : [answerElement.DOCS];
        docs.push(...docList);
      }
      // Note: We do NOT recursively collect from nested questions here
      // because nested QuestionnaireComp components have their own state
      // and will call onRequiredDocs independently
    }

    return docs;
  }, [selectedAnswer, isMultichoice]);

  useEffect(() => {
    const docs = collectDocs(element);
    if (JSON.stringify(docs) !== JSON.stringify(currentDocs)) {
      setCurrentDocs(docs);
      onRequiredDocs?.(docs, componentKey);
    }
  }, [selectedAnswer, element, isMultichoice, collectDocs, currentDocs, onRequiredDocs, componentKey]);

  const handleRadioChange = (value: string) => {
    if (isMultichoice) {
      const currentAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : [];
      const newAnswers = currentAnswers.includes(value)
        ? currentAnswers.filter(answer => answer !== value)
        : [...currentAnswers, value];
      
      
      setSelectedAnswer(newAnswers);
      if (!currentPromptField) setInputValue("");

      // Use the recursive collectDocs function to get DOCS from all selected answers and nested questions
      const allDocs = collectDocs(element, newAnswers);

      setCurrentDocs(allDocs);
      onRequiredDocs?.(allDocs, componentKey);
    } else {
      const previousAnswer = selectedAnswer as string;

      setSelectedAnswer(value);
      if (!currentPromptField) setInputValue("");

      // Use the recursive collectDocs function to get DOCS from selected answer and nested questions
      const allDocs = collectDocs(element, value);

      setCurrentDocs(allDocs);
      onRequiredDocs?.(allDocs, componentKey);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (currentDocs.length > 0) {
      onRequiredDocs?.(currentDocs, componentKey);
    } else {
      // If no docs, still report empty to clear any previous docs from this component
      onRequiredDocs?.([], componentKey);
    }
  };

  const isCurrentPathComplete = (): boolean => {
    if (isMultichoice) {
      if (!Array.isArray(selectedAnswer) || selectedAnswer.length === 0) return false;
      if (currentPromptField && !inputValue.trim()) return false;

      // For multichoice, check if any selected answer has a complete path
      return selectedAnswer.some(answer => {
        const answerValue = element.ANSWER?.[answer];
        if (!answerValue) return false;

        if (answerValue.DOCS && !answerValue.QUESTION) return true;
        if (answerValue.QUESTION) return false;

        return true;
      });
    } else {
      if (!selectedAnswer) return false;
      if (currentPromptField && !inputValue.trim()) return false;

      const answerValue = selectedAnswer ? element.ANSWER?.[selectedAnswer as string] : null;
      if (!answerValue) return false;

      if (answerValue.DOCS && !answerValue.QUESTION) return true;
      if (answerValue.QUESTION) return false;

      return true;
    }
  };

  // Create radio options with disabled state information
  const radioOptions = answerEntries.map(([key, value]) => {
    let disabledReason = null;
    
    if (value.disabled) {
      // Provide specific reasons based on business logic and context
      if (key === "Household") {
        // Check if this is in the context of FDA regulation
        const isFDARegulated = element.title?.includes("FDA") || 
                              element.title?.includes("regulated by");
        if (isFDARegulated) {
          disabledReason = "Household products are not regulated by FDA and not applicable for this path";
        } else {
          disabledReason = "Household products are not applicable for this regulatory path";
        }
      } else if (key === "Seafood" && element.title?.includes("FSMA")) {
        disabledReason = "Seafood products have different FSMA requirements";
      } else if (key === "Supplements" && element.title?.includes("FDA")) {
        disabledReason = "Supplements have different FDA regulatory requirements";
      } else {
        disabledReason = "This option is not available for your current selection";
      }
    }
    
    const option = { 
      label: key, 
      value: key, 
      disabled: value.disabled || false,
      disabledReason,
      tooltip: value.tooltip || ""
    };
    
    return option;
  });
  const getNextQuestion = () => {
    const branchingAnswer = getPrimaryBranchingAnswer(element, selectedAnswer);
    if (!branchingAnswer) return null;
    return element.ANSWER?.[branchingAnswer]?.QUESTION || null;
  };

  const nextQuestion = getNextQuestion();
  const nextPromptField = nextQuestion?.promptField || nextQuestion?.ANSWER?.promptField;

  // Keep answerValue for backward compatibility
  const answerValue = isMultichoice 
    ? (() => {
        const firstSelected = Array.isArray(selectedAnswer) ? selectedAnswer?.[0] : undefined;
        return firstSelected ? element.ANSWER?.[firstSelected] : null;
      })()
    : (selectedAnswer ? element.ANSWER?.[selectedAnswer as string] : null);

  return {
    darkModeClass,
    selectedAnswer,
    inputValue,
    currentDocs,
    answerEntries,
    promptField: currentPromptField,
    answerValue,
    nextQuestion,
    nextPromptField,
    radioOptions,
    isCurrentPathComplete,
    handleRadioChange,
    handleInputChange,
    isMultichoice
  };
};

// HOOK 3: useRequiredDocs
export const useRequiredDocs = ({
  docs
}: {
  docs: Doc[];
}) => {
  const validateFile = useFileValidator({ allowedTypes: ["pdf"] });
  const [fileSelected, setFileSelected] = useState<{ [key: string]: File | null }>({});
  const [fileInputKeys, setFileInputKeys] = useState<{ [key: string]: number }>({});
  const [showErrors, setShowErrors] = useState<{ [key: string]: boolean }>({});
  const [assetIdsByKey, setAssetIdsByKey] = useState<{ [key: string]: number | undefined }>({});
  const [isUploadingByKey, setIsUploadingByKey] = useState<{ [key: string]: boolean }>({});

  // Error validation removed - will be handled by new centralized system

  const handleFileChange = (docId: number, key: string, file: File | null): boolean => {
    const fileKey = `${docId}-${key}`;
    
    

    if (!file) {

      setFileSelected(prev => ({ ...prev, [fileKey]: null }));
      setShowErrors(prev => ({ ...prev, [fileKey]: true }));
      setAssetIdsByKey(prev => {
        const updated = { ...prev, [fileKey]: undefined };
        return updated;
      });
      return true;
    }

    if (!validateFile(file)) {

      setFileInputKeys(prev => ({
        ...prev,
        [fileKey]: (prev[fileKey] || 0) + 1
      }));
      return false;
    }


    
    // Start upload to get asset id
    setIsUploadingByKey(prev => ({ ...prev, [fileKey]: true }));
    (async () => {
      try {

        // Convert to base64 like Other Attachments
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const dataPart = result?.split(",")[1] || "";
            resolve(dataPart);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
        

        const resp = await (await import("@/services/tastingPanelService")).default.addProductPhotos({
          filename: file.name,
          type: "document",
          data: base64
        } as any);
        
        const assetId = Number(resp?.id);
       
        
        
        const newAssetId = Number.isFinite(assetId) ? assetId : undefined;

        setAssetIdsByKey(prev => {
          const updated = { ...prev, [fileKey]: newAssetId };
          return updated;
        });
        setFileSelected(prev => ({ ...prev, [fileKey]: file }));
        setShowErrors(prev => ({ ...prev, [fileKey]: false }));
      } catch (e) {
        console.error(`❌ [RequiredDocs] Upload failed for ${fileKey}:`, e);
        // reset selection on failure
        setFileSelected(prev => ({ ...prev, [fileKey]: null }));
        setShowErrors(prev => ({ ...prev, [fileKey]: true }));
        setFileInputKeys(prev => ({ ...prev, [fileKey]: (prev[fileKey] || 0) + 1 }));
      } finally {
        setIsUploadingByKey(prev => ({ ...prev, [fileKey]: false }));

      }
    })();
    return true;
  };

  return {
    fileSelected,
    fileInputKeys,
    showErrors,
    handleFileChange,
    assetIdsByKey,
    isUploadingByKey
  };
};

// HOOK 4: useGeneralFoodSafetyDocs
export const useGeneralFoodSafetyDocs = ({
  resetTrigger
}: {
  resetTrigger?: number;
  siteData?: any;
}) => {
  const validateFile = useFileValidator({ allowedTypes: ["pdf"] });
  const [fileSelected, setFileSelected] = useState<{ [key: string]: File | null }>({});
  const [fileInputKeys, setFileInputKeys] = useState<{ [key: string]: number }>({});
  const [showErrors, setShowErrors] = useState<{ [key: string]: boolean }>({});
  const [assetIdsByKey, setAssetIdsByKey] = useState<{ [key: string]: number | undefined }>({});
  const [isUploadingByKey, setIsUploadingByKey] = useState<{ [key: string]: boolean }>({});

  // Define required fields
  const requiredFields = [
    "productSpecification",
    "processFlowDiagram", 
    "hazardAnalysis",
    "preventativeCriticalControlTables",
    "pcqiHaccpCertificate"
  ];

  // Reset form when resetTrigger changes
  useEffect(() => {
    if (resetTrigger) {
      setFileSelected({});
      setFileInputKeys({});
      setShowErrors({});
    }
  }, [resetTrigger]);

  // Error validation removed - will be handled by new centralized system

  const handleFileChange = (fieldKey: string, file: File | null): boolean => {

    if (!file) {
      setFileSelected(prev => ({ ...prev, [fieldKey]: null }));
      setAssetIdsByKey(prev => ({ ...prev, [fieldKey]: undefined }));
      if (requiredFields.includes(fieldKey)) {
        setShowErrors(prev => ({ ...prev, [fieldKey]: true }));
      }
      return true;
    }

    if (!validateFile(file)) {
      setFileInputKeys(prev => ({
        ...prev,
        [fieldKey]: (prev[fieldKey] || 0) + 1
      }));
      return false;
    }

    // Upload file to get asset id (reuse tasting panel service upload)
    setIsUploadingByKey(prev => ({ ...prev, [fieldKey]: true }));
    (async () => {
      try {

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const dataPart = result?.split(",")[1] || "";
            resolve(dataPart);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
        

        const resp = await (await import("@/services/tastingPanelService")).default.addProductPhotos({
          filename: file.name,
          type: "document",
          data: base64
        } as any);
        
        const assetId = Number(resp?.id);
        
        setAssetIdsByKey(prev => ({ ...prev, [fieldKey]: Number.isFinite(assetId) ? assetId : undefined }));
        setFileSelected(prev => ({ ...prev, [fieldKey]: file }));
        setShowErrors(prev => ({ ...prev, [fieldKey]: false }));
      } catch (e) {
        console.error(`❌ [GeneralFoodSafetyDocs] Upload failed for ${fieldKey}:`, e);
        // Reset selection on failure
        setFileSelected(prev => ({ ...prev, [fieldKey]: null }));
        setShowErrors(prev => ({ ...prev, [fieldKey]: true }));
        setFileInputKeys(prev => ({ ...prev, [fieldKey]: (prev[fieldKey] || 0) + 1 }));
      } finally {
        setIsUploadingByKey(prev => ({ ...prev, [fieldKey]: false }));

      }
    })();
    return true;
  };

  return {
    fileSelected,
    fileInputKeys,
    showErrors,
    handleFileChange,
    assetIdsByKey,
    isUploadingByKey
  };
};
